<script setup lang="ts">
/** Section « Administratif » du formulaire de création — client + coordonnées. */
import { computed } from 'vue'
import { IdentificationIcon } from '@heroicons/vue/24/outline'
import AccompClientPicker from '@/v2/accompagnement/components/AccompClientPicker.vue'
import type { AccompClientRef } from '@/v2/accompagnement/api/accompagnementTypes'

defineProps<{ clientError?: string }>()
const client = defineModel<AccompClientRef | null>('client', { required: true })

const address = computed({
  get: () => client.value?.address ?? '',
  set: (val: string) => {
    if (client.value) client.value = { ...client.value, address: val.trim() ? val : null }
  },
})

const naf = computed({
  get: () => client.value?.naf ?? '',
  set: (val: string) => {
    if (client.value) client.value = { ...client.value, naf: val.trim() ? val.trim() : null }
  },
})
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="accomp-admin-title">
    <div class="card-body gap-4 p-4 md:p-6">
      <h2 id="accomp-admin-title" class="card-title flex items-center gap-2 text-lg font-semibold">
        <IdentificationIcon class="h-5 w-5 text-primary" aria-hidden="true" />
        Administratif
      </h2>

      <div>
        <label for="accomp-client" class="mb-1 block text-sm font-medium text-base-content">
          Client <span class="text-error" aria-hidden="true">*</span>
        </label>
        <AccompClientPicker id="accomp-client" v-model="client" :invalid="!!clientError" />
        <p v-if="clientError" class="mt-1 text-sm text-error">{{ clientError }}</p>
        <p v-else class="mt-1 text-sm text-base-content/70">
          Recherchez par raison sociale, SIREN ou SIRET — les informations sont reprises automatiquement.
        </p>
      </div>

      <!-- Coordonnées reprises / complétées -->
      <div v-if="client" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label class="mb-1 block text-sm font-medium text-base-content/70">Raison sociale</label>
          <p class="rounded-lg border border-base-300 bg-base-200/60 px-3 py-2 text-base">{{ client.name }}</p>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-base-content/70">SIREN</label>
          <p class="rounded-lg border border-base-300 bg-base-200/60 px-3 py-2 text-base tabular-nums">
            {{ client.siren ?? '—' }}
          </p>
        </div>
        <div>
          <label for="accomp-naf" class="mb-1 block text-sm font-medium text-base-content">Code NAF / APE</label>
          <input
            id="accomp-naf"
            v-model="naf"
            type="text"
            class="input input-bordered w-full"
            placeholder="Ex. 4711B"
            aria-describedby="accomp-naf-help"
          />
          <p id="accomp-naf-help" class="mt-1 text-sm text-base-content/70">
            <template v-if="client.nafLabel">{{ client.nafLabel }}</template>
            <template v-else>Repris de la fiche client lorsque disponible.</template>
          </p>
        </div>
        <div class="sm:col-span-2">
          <label for="accomp-address" class="mb-1 block text-sm font-medium text-base-content">Adresse</label>
          <input
            id="accomp-address"
            v-model="address"
            type="text"
            class="input input-bordered w-full"
            placeholder="Adresse d'intervention (facultatif)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
